import { useState } from 'react';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { FloatingActionBar } from '@frontend/adapto-actions';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { useTimestampFormatter } from '@frontend/timestamp-formatter';
import { FaxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  ListRow,
  ListRowProps,
  PopoverMenu,
  PopoverMenuItem,
  usePopoverMenu,
  Text,
  UsePopoverMenuResponse,
  Button,
  SpinningLoader,
  ButtonSize,
} from '@frontend/design-system';
import { ListRowDownloadPopover } from './list-row-download-popover';
import { ListRowPreviewPopover } from './list-row-preview-popover';

dayjs.extend(calendar);
type FaxListRowProps = ListRowProps<'div'> & {
  Lead: JSX.Element;
  Title: JSX.Element;
  Content?: JSX.Element;
  timestamp?: string;
  isExpanded?: boolean;
  ExpandedContent?: JSX.Element;
  actions?: {
    actionIcon: PopoverMenuItem['Icon'];
    label: string;
    action: () => void;
  }[];
  archiveButtonProps?: ArchiveButtonProps['archiveButtonProps'];
  actionsPopoverProps: UsePopoverMenuResponse<HTMLElement, false, HTMLMenuElement, HTMLButtonElement>;
  summaryButtonProps?: SummaryButtonProps['summaryButtonProps'];
  hideInlineActions?: boolean;
};

export const FaxListRow = ({
  Lead,
  Title,
  Content,
  isExpanded,
  ExpandedContent,
  timestamp,
  actions,
  archiveButtonProps,
  trackingId,
  actionsPopoverProps,
  summaryButtonProps,
  hideInlineActions,
  ...rest
}: FaxListRowProps) => {
  const [isHovering, setIsHovering] = useState(false);

  const { getTriggerProps, getMenuProps, getItemProps, isOpen, close } = actionsPopoverProps;

  const formatTimestamp = useTimestampFormatter();

  return (
    <ListRow
      trackingId={trackingId}
      as='div'
      css={{ gridTemplateColumns: 'auto 1fr' }}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        if (!isOpen) {
          setIsHovering(false);
        }
      }}
      {...rest}
    >
      {Lead}
      <ListRow.Content css={{ display: 'flex', flexDirection: 'column', rowGap: theme.spacing(1), minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minWidth: 0,
            columnGap: theme.spacing(3),
          }}
        >
          {Title}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {!hideInlineActions && (
              <div
                style={{
                  position: 'absolute',
                  right: theme.spacing(3),
                  width: '60px',
                  display: 'flex',
                  justifyContent: 'end',
                  alignItems: 'center',
                  columnGap: theme.spacing(0.25),
                  visibility: isHovering ? 'visible' : 'hidden',
                  opacity: isHovering ? 1 : 0,
                  transition: 'all 200ms ease-in-out',
                }}
              >
                {archiveButtonProps && <ArchiveIconButton archiveButtonProps={archiveButtonProps} />}
                {actions && (
                  <ActionsPopoverMenu
                    actions={actions}
                    getMenuProps={getMenuProps}
                    getItemProps={getItemProps}
                    getTriggerProps={getTriggerProps}
                    close={close}
                  />
                )}
                {summaryButtonProps && <SummaryIconButton summaryButtonProps={summaryButtonProps} />}
              </div>
            )}
            <Text
              size='small'
              color={'light'}
              weight={'regular'}
              css={{
                flexShrink: 0,
                width: 'max-content',
                visibility: isHovering ? 'hidden' : 'visible',
                opacity: isHovering ? 0 : 1,
                transition: 'all 200ms ease-in-out',
              }}
            >
              {formatTimestamp(timestamp)}
            </Text>
          </div>
        </div>
        {Content}
        {isExpanded && ExpandedContent}
      </ListRow.Content>
    </ListRow>
  );
};

type ActionsPopoverMenuProps = {
  actions: {
    actionIcon: PopoverMenuItem['Icon'];
    label: string;
    action: () => void;
    disabled?: boolean;
    disabledTooltipText?: string;
    trackingId?: string;
    disableCloseOnSelect?: boolean;
    popoverProps?: UsePopoverMenuResponse<HTMLButtonElement, false, HTMLMenuElement, HTMLButtonElement>;
    faxId?: string;
    locationId?: string;
    order?: number;
  }[];
  getItemProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getItemProps'];
  getMenuProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getMenuProps'];
  getTriggerProps: ReturnType<typeof usePopoverMenu<HTMLElement>>['getTriggerProps'];
  close: () => void;
  size?: ButtonSize;
};

export const ActionsPopoverMenu = ({
  actions,
  getItemProps,
  getTriggerProps,
  getMenuProps,
  close,
  size,
}: ActionsPopoverMenuProps) => {
  const { t } = useTranslation('fax');
  const menuProps = getMenuProps();

  const triggerProps = getTriggerProps({
    onClick: (e) => {
      e.stopPropagation();
    },
  });

  return (
    <>
      <Button
        css={{
          padding: theme.spacing(0.25),
          svg: {
            color: theme.colors.neutral70,
          },
          ':hover:not(:disabled)': {
            background: 'none',
            svg: {
              color: theme.colors.primary50,
            },
          },
        }}
        size={size ?? 'large'}
        iconName='more'
        label={t('More actions')}
        variant='tertiary'
        trackingId={`${FaxPrefixes.List}-more-actions-button`}
        {...triggerProps}
      />

      <PopoverMenu {...menuProps}>
        {actions.map(
          (
            {
              actionIcon,
              label,
              action,
              disabled = false,
              disabledTooltipText,
              trackingId,
              disableCloseOnSelect = false,
              popoverProps,
              faxId,
              locationId,
              order = 0,
            },
            index
          ) => {
            return (
              <>
                {disableCloseOnSelect ? (
                  <>
                    <PopoverMenuItem
                      css={{
                        width: '185px',
                        borderBottom: order === 1 ? `1px solid ${theme.colors.neutral20}` : 'none',
                        marginBottom: order === 1 ? theme.spacing(1) : '0',
                        paddingBottom: order === 1 ? theme.spacing(1) : '0',
                      }}
                      Icon={actionIcon}
                      {...popoverProps?.getItemProps({
                        index: order,
                        disableCloseOnSelect: true,
                      })}
                      {...popoverProps?.getTriggerProps()}
                      trackingId={trackingId}
                    >
                      <div
                        css={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          flex: 1,
                        }}
                      >
                        <Text>{label}</Text>
                        <Icon
                          name='alt-caret-right-tiny'
                          css={{
                            color: theme.colors.neutral50,
                          }}
                        />
                      </div>
                    </PopoverMenuItem>
                    {order === 0 && (
                      <ListRowPreviewPopover
                        closeParentMenu={close}
                        previewPopoverProps={popoverProps}
                        faxId={faxId ?? ''}
                        locationId={locationId ?? ''}
                      />
                    )}
                    {order === 1 && (
                      <ListRowDownloadPopover
                        closeParentMenu={close}
                        downloadPopoverProps={popoverProps}
                        faxId={faxId ?? ''}
                        locationId={locationId ?? ''}
                      />
                    )}
                  </>
                ) : (
                  <PopoverMenuItem
                    Icon={actionIcon}
                    {...(disabled
                      ? getItemProps({
                          index,
                        })
                      : getItemProps({
                          index,
                          onClick: (e) => {
                            e.stopPropagation();
                            action();
                          },
                        }))}
                    disabled={disabled}
                    hoverLabel={disabled ? disabledTooltipText : undefined}
                    key={label}
                    trackingId={trackingId}
                  >
                    {label}
                  </PopoverMenuItem>
                )}
              </>
            );
          }
        )}
      </PopoverMenu>
    </>
  );
};

type ArchiveButtonProps = {
  archiveButtonProps: {
    label: string;
    onClick: () => void;
    iconName: IconName;
    trackingId?: string;
  };
  size?: ButtonSize;
};

const ArchiveIconButton = ({ archiveButtonProps, size }: ArchiveButtonProps) => {
  const { label, onClick, iconName, trackingId } = archiveButtonProps;

  return (
    <Button
      css={{
        svg: {
          color: theme.colors.neutral70,
        },
        ':hover:not(:disabled)': {
          background: 'none',
          svg: {
            color: theme.colors.primary50,
          },
        },
      }}
      size={size ?? 'small'}
      iconName={iconName}
      hoverLabel={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      trackingId={trackingId}
      variant='tertiary'
    />
  );
};

type SummaryButtonProps = {
  summaryButtonProps: {
    label: string;
    trackingId?: string;
    allowAISummary?: boolean;
    isLoading: boolean;
    hasSummary: boolean;
    getPopoverTriggerProps?: UsePopoverMenuResponse<
      HTMLButtonElement,
      false,
      HTMLMenuElement,
      HTMLButtonElement
    >['getTriggerProps'];
  };
  size?: ButtonSize;
};

const SummaryIconButton = ({ summaryButtonProps, size }: SummaryButtonProps) => {
  const { label, trackingId, allowAISummary, isLoading, getPopoverTriggerProps } = summaryButtonProps;

  if (isLoading) {
    return (
      <div
        css={{
          display: 'flex',
          alignItems: 'center',
          height: 34,
          width: 34,
          padding: theme.spacing(1, 1, 1, 0),
        }}
      >
        <SpinningLoader size='small' />
      </div>
    );
  }

  return (
    <Button
      css={{
        ':hover:not(:disabled)': { background: theme.colors.white, color: theme.colors.primary50 },
      }}
      size={size ?? 'medium'}
      iconName='aisummary'
      label={label}
      variant='secondary'
      trackingId={trackingId}
      {...(getPopoverTriggerProps && allowAISummary ? getPopoverTriggerProps() : {})}
    />
  );
};

export const FloatingFaxListRow = (props: FaxListRowProps) => {
  const { actions, archiveButtonProps, actionsPopoverProps, summaryButtonProps } = props;

  return (
    <FloatingActionBar>
      <FloatingActionBar.Actions>
        {archiveButtonProps && <ArchiveIconButton archiveButtonProps={archiveButtonProps} size='small' />}
        {actions && (
          <ActionsPopoverMenu
            actions={actions}
            getMenuProps={actionsPopoverProps.getMenuProps}
            getItemProps={actionsPopoverProps.getItemProps}
            getTriggerProps={actionsPopoverProps.getTriggerProps}
            close={actionsPopoverProps.close}
            size='small'
          />
        )}
        {summaryButtonProps && <SummaryIconButton summaryButtonProps={summaryButtonProps} size='small' />}
      </FloatingActionBar.Actions>
      <FloatingActionBar.Content>
        <FaxListRow {...props} hideInlineActions />
      </FloatingActionBar.Content>
    </FloatingActionBar>
  );
};

// TODO: update loader with gradients in the design system
// probably need new AiLoader
// const aiSummaryStyles = {
//   "div[role='progressbar'] > div:nth-of-type(1)": {
//     borderColor: `${theme.colorsV2.tangerine40()} ${theme.colorsV2.tangerine40()} transparent transparent`,
//   },
//   "div[role='progressbar'] > div:nth-of-type(2)": {
//     borderColor: `${theme.colorsV2.tangerine50()} ${theme.colorsV2.seaweed50()} transparent transparent`,
//   },
//   "div[role='progressbar'] > div:nth-of-type(3)": {
//     borderColor: `${theme.colorsV2.indigo50()} ${theme.colorsV2.indigo50()}  transparent transparent`,
//   },
// };

// TODO: cannot fill a gradient on the svg right now,
// const hasSummaryStyles = {
//   'span > svg': {
//     color: `linear-gradient(45.06deg, #FF7301 2.26%, #B245D1 50%, #018479 97.74%)`,
//     fill: `linear-gradient(45.06deg, #FF7301 2.26%, #B245D1 50%, #018479 97.74%)`,
//   },
// };
